import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Bell, BellRing, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import {
    isPushSupported,
    isIOS,
    isStandalone,
    getCurrentSubscription,
    enablePush,
} from "../lib/push";

/** Animated bell shown until the user enables notifications.
 *  Tapping opens an in-place modal with an "Enable" button — no page navigation.
 *  Hidden when:
 *   - browser doesn't support push, or
 *   - permission is denied, or
 *   - already subscribed, or
 *   - iOS Safari outside PWA (banner handles that case)
 */
export default function NotificationBell() {
    const [eligible, setEligible] = useState(false);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const location = useLocation();

    const refresh = useCallback(async () => {
        if (!isPushSupported()) return setEligible(false);
        if (Notification.permission === "denied") return setEligible(false);
        if (isIOS() && !isStandalone()) return setEligible(false);
        const sub = await getCurrentSubscription();
        setEligible(!sub);
    }, []);

    useEffect(() => {
        refresh();
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, [refresh]);

    useEffect(() => { refresh(); }, [location.pathname, refresh]);

    const handleEnable = async () => {
        setBusy(true);
        try {
            await enablePush();
            toast.success("Notifications enabled. You're on the list.");
            setOpen(false);
            await refresh();
        } catch (err) {
            toast.error(err.message || "Could not enable notifications.");
        } finally {
            setBusy(false);
        }
    };

    if (!eligible) return null;

    const showIosHint = isIOS() && !isStandalone();

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Enable notifications"
                className="relative inline-flex items-center justify-center w-10 h-10 hover:bg-white/5 transition-colors"
                style={{ borderRadius: 2 }}
                data-testid="notification-bell"
            >
                <Bell size={20} className="text-[#FF9F0A] animate-bell-shake" />
                <span
                    aria-hidden="true"
                    className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#FF3B30] rounded-full animate-bell-dot"
                />
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={() => !busy && setOpen(false)}
                    data-testid="enable-notifications-modal"
                >
                    <div
                        className="bg-[#0A0A0A] border border-white/10 max-w-sm w-full p-6 relative"
                        style={{ borderRadius: 2 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setOpen(false)}
                            disabled={busy}
                            aria-label="Close"
                            className="absolute top-3 right-3 text-zinc-500 hover:text-white"
                            data-testid="enable-modal-close"
                        >
                            <X size={18} />
                        </button>

                        <div className="w-12 h-12 bg-[#FF9F0A]/15 border border-[#FF9F0A]/40 flex items-center justify-center" style={{ borderRadius: 2 }}>
                            <BellRing size={22} className="text-[#FF9F0A]" />
                        </div>
                        <div className="label-tactical text-[#FF9F0A] mt-4">STAY IN THE GAME</div>
                        <h3 className="heading text-2xl uppercase font-black mt-1 leading-tight" data-testid="enable-modal-title">
                            Enable Notifications
                        </h3>
                        <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                            Get a tap when your streak is about to break, a competition is closing, or
                            your daily quota is nearly due. You can turn this off any time from Profile.
                        </p>

                        {showIosHint && (
                            <div className="mt-4 border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-3 text-xs text-[#FF9F0A]" style={{ borderRadius: 2 }}>
                                <div className="flex items-start gap-2">
                                    <Smartphone size={14} className="mt-0.5 flex-shrink-0" />
                                    <div className="text-zinc-300 normal-case font-normal">
                                        On iOS you must first <strong>Add to Home Screen</strong> from the Safari Share menu, then open the Nerdy app icon and try again.
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex gap-2 mt-6">
                            <button
                                onClick={() => setOpen(false)}
                                disabled={busy}
                                className="btn-ghost flex-1 min-h-[44px] justify-center disabled:opacity-40"
                                data-testid="enable-modal-later"
                            >
                                LATER
                            </button>
                            <button
                                onClick={handleEnable}
                                disabled={busy || showIosHint}
                                className="btn-blaze flex-1 min-h-[44px] justify-center disabled:opacity-40"
                                data-testid="enable-modal-confirm"
                            >
                                <Bell size={14} /> {busy ? "ENABLING…" : "ENABLE"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
